import * as React from 'react';

/**
 * Generic icon+label status pill, extracted from the Patient Profile's
 * `GutCheckAppStatus` (careos-prototype.css tokens, not a Chakra recipe) so
 * every surface rendering a member-status badge — the Gut Checks card today,
 * the roster's Care Coordination column now — draws from one implementation
 * instead of each inventing its own colors. See the design-system audit's
 * former "second status vocabulary" callout for why this existed twice.
 *
 * Deliberately not a Chakra `defineRecipe` wrapper like most of `ui/`: it
 * renders via the same raw CSS-custom-property layer
 * (`PatientProfile/careos-prototype.css`, scoped under `.careos-root`) the
 * careOS surfaces already use, so a status's colors stay defined once in
 * each domain's own `fg`/`tint` map rather than becoming new design tokens.
 * Any consumer needs to render inside a `.careos-root` ancestor for the CSS
 * vars in its `fg`/`tint` values to resolve — the roster's list already is,
 * for its scrollbar styling.
 */

export type StatusIndicatorPillProps = {
  /** Visible text — omitted entirely when `compact`. */
  label: string;
  /** Longer explanation, appended to the tooltip/aria-label. */
  description?: string;
  /** Icon + label color — a CSS color or `var(--token)`. */
  fg: string;
  /** Pill fill — typically a soft rgba tint derived from `fg`. */
  tint: string;
  icon: React.ReactNode;
  /**
   * Icon-only, no visible label — for contexts with an adjacent cue that
   * would make the text redundant (see `GutCheckAppStatus`'s
   * `baseline-complete`). Full icon+label pill otherwise.
   */
  compact?: boolean;
  style?: React.CSSProperties;
};

export const StatusIndicatorPill = ({
  label,
  description,
  fg,
  tint,
  icon,
  compact = false,
  style,
}: StatusIndicatorPillProps) => {
  const ariaLabel = description ? `${label}. ${description}` : label;
  const title = description ? `${label} — ${description}` : label;

  if (compact) {
    return (
      <span
        role="img"
        aria-label={ariaLabel}
        title={title}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          flexShrink: 0,
          borderRadius: 'var(--radius-pill)',
          background: tint,
          color: fg,
          ...style,
        }}
      >
        {icon}
      </span>
    );
  }

  return (
    <span
      role="img"
      aria-label={ariaLabel}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: tint,
        color: fg,
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-xs-b-size)',
        fontWeight: 'var(--text-xs-b-weight)',
        lineHeight: 'var(--text-xs-b-lh)',
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {icon}
      {label}
    </span>
  );
};

export default StatusIndicatorPill;
